import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { addToast } from '../store/ducks/toast/actions';
import { EToastTypes } from '../store/ducks/toast/types';
import { useWishlistService } from '../services/MVP/useWishlistService';
import useDebounce from './useDebounce';
import { sendTagging } from '../store/ducks/tagging/actions';

export interface FavoriteTaggingData {
  name: string;
  category: string;
  price?: string;
  quantity?: string;
  um?: string;
  isAgreementPrice?: boolean;
}

export function useFavoriteToggle(
  initialState: boolean,
  sku: string,
  taggingData?: FavoriteTaggingData,
  screenName?: string
) {
  const dispatch = useDispatch();
  const { addToWishlist, removeFromWishlist } = useWishlistService();
  const [isFavorited, setIsFavorited] = useState<boolean>(initialState);
  const isFavoritedRef = useRef(initialState);

  const syncWithApi = useDebounce(async () => {
    const willFavorite = isFavoritedRef.current;
    const success = willFavorite
      ? await addToWishlist(sku)
      : await removeFromWishlist(sku);

    if (success) {
      dispatch(
        addToast({
          type: willFavorite ? EToastTypes.SUCCESS : EToastTypes.DANGER,
          message: willFavorite
            ? 'Produto adicionado aos Favoritos'
            : 'Produto removido dos Favoritos',
          durationInMS: 5000
        })
      );
    } else {
      isFavoritedRef.current = !willFavorite;
      setIsFavorited(!willFavorite);
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Não foi possível atualizar os favoritos. Tente novamente.',
          durationInMS: 5000
        })
      );
    }
  }, 300);

  const handleToggleFavorite = () => {
    const willFavorite = !isFavoritedRef.current;
    isFavoritedRef.current = willFavorite;
    setIsFavorited(willFavorite);

    if (taggingData) {
      const resolvedScreenName = screenName ?? 'vitrine';
      const tipoCompra =
        resolvedScreenName === 'pagina_favoritos' ? 'favoritos' : 'vitrine';
      dispatch(
        sendTagging({
          event: willFavorite ? 'adicionar_favorito' : 'remover_favorito',
          screenName: resolvedScreenName,
          ecommerce: {
            items: [
              {
                item_id: sku,
                item_name: taggingData.name,
                item_category: taggingData.category,
                ...(taggingData.price && Number(taggingData.price) > 0
                  ? { price: taggingData.price }
                  : {}),
                quantity: '1',
                unidade_de_medida: taggingData.um ?? '',
                cod_gerdau: sku,
                preco_acordado: taggingData.isAgreementPrice ?? false,
                screenName: resolvedScreenName,
                tipo_compra: tipoCompra,
                favoritado: willFavorite
              }
            ]
          }
        })
      );
    }

    (syncWithApi as () => void)();
  };

  return { isFavorited, handleToggleFavorite };
}
